import axios from 'axios';

const API_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5001/api';

const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refreshToken';

const api = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// --- automatic refresh on 401 (single in-flight refresh, queued retries) ---
let refreshPromise = null;

async function refreshTokens() {
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    if (!refreshToken) throw new Error('No refresh token');
    // raw axios — must not go through the 401 interceptor
    const response = await axios.post(`${API_URL}/users/refresh`, { refreshToken });
    localStorage.setItem(TOKEN_KEY, response.data.token);
    localStorage.setItem(REFRESH_KEY, response.data.refreshToken);
    return response.data.token;
}

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;
        const status = error.response?.status;

        if (status !== 401 || original?._retried) {
            return Promise.reject(error);
        }

        if (!localStorage.getItem(REFRESH_KEY)) {
            // No refresh token to fall back on: the access token itself is
            // rejected (expired, malformed, or its user no longer exists —
            // see auth.js). There's nothing left to try — end the session
            // here instead of silently leaving the app half-authenticated.
            localStorage.removeItem(TOKEN_KEY);
            if (!window.location.pathname.startsWith('/login')) {
                window.location.assign('/login');
            }
            return Promise.reject(error);
        }

        original._retried = true;
        try {
            refreshPromise = refreshPromise || refreshTokens();
            const newToken = await refreshPromise;
            refreshPromise = null;
            original.headers.Authorization = `Bearer ${newToken}`;
            return api(original);
        } catch (refreshError) {
            refreshPromise = null;
            // refresh failed — session is over, go to login
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_KEY);
            if (!window.location.pathname.startsWith('/login')) {
                window.location.assign('/login');
            }
            return Promise.reject(refreshError);
        }
    },
);

function setDefaultCommonHeaders(key, value) {
    api.defaults.headers.common[key] = value;
}

const httpServices = {
    setDefaultCommonHeaders,
    get: (url, config) => api.get(url, config),
    post: (url, data, config) => api.post(url, data, config),
    put: (url, data) => api.put(url, data),
    patch: (url, data) => api.patch(url, data),
    delete: (url) => api.delete(url),
};

export default httpServices;
